import { Link } from "react-router-dom";
import { Phone, ArrowRight, ShieldCheck, MapPin, Clock, ImagePlus } from "lucide-react";
import { SITE } from "@/data/site";
import { Seo } from "@/components/Seo";
import { Reveal, RevealStagger } from "@/components/Reveal";
import { CtaFinal } from "@/components/CtaFinal";

export default function AboutPage() {
  return (
    <>
      <Seo
        title="À propos — Brandon Pouvit | HRC Rénovation Dordogne"
        description="Brandon Pouvit, artisan rénovation depuis 10 ans en Dordogne. Ravalement, hydrofuge, peinture. Devis gratuit. ☎ 06 41 96 83 46."
        jsonLd={[{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Accueil", item: "https://hrcrenovation.fr/" },
            { "@type": "ListItem", position: 2, name: "À propos" },
          ],
        }]}
      />
      <section className="pt-[120px] pb-16 lg:pb-24 bg-surface-canvas noise-overlay relative" style={{ backgroundImage: "radial-gradient(circle at 100% 0%, rgba(30,79,168,0.08) 0%, transparent 50%)" }}>
        <div className="max-w-site mx-auto px-5 lg:px-6 grid grid-cols-1 lg:grid-cols-[55fr_45fr] gap-12 items-center">
          <Reveal>
            <p className="micro-label mb-4">À propos</p>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-[56px] leading-[1.05] text-brand-deep" style={{ letterSpacing: "-0.03em" }} data-testid="about-h1">
              Un artisan qui fait ce qu'il dit.
            </h1>
            <p className="mt-6 text-lg text-ink-secondary max-w-[540px]">
              HRC Rénovation, c'est Brandon Pouvit. 10 ans de chantiers en Dordogne et alentours, 90% chez des particuliers comme vous.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <a href={SITE.phoneHref} className="btn-base btn-metal tnum" data-testid="about-phone-btn">
                <Phone size={18} aria-hidden="true" />
                {SITE.phone}
              </a>
              <Link to="/devis" className="btn-base btn-outline-deep">
                Demander un devis
                <ArrowRight size={18} aria-hidden="true" />
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            {/* TODO: Remplacer par une vraie photo de Brandon sur chantier.
                NE PAS générer d'image IA. NE PAS utiliser de photo stock d'artisan. */}
            <div className="rounded-2xl overflow-hidden shadow-elevated border border-line">
              <div className="photo-placeholder" style={{ aspectRatio: "4/5" }}>
                <span className="w-11 h-11 rounded-full bg-brand-highlight flex items-center justify-center">
                  <ImagePlus size={20} className="text-brand-royal" aria-hidden="true" />
                </span>
                <span className="font-medium text-ink-secondary">Photo à venir</span>
                <span className="dashed-zone">Portrait ou photo chantier réelle</span>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16 lg:py-28 bg-surface-canvas">
        <div className="max-w-[780px] mx-auto px-5 lg:px-6">
          <Reveal>
            <p className="micro-label mb-4">L'histoire</p>
            <h2 className="font-heading font-bold text-3xl lg:text-4xl text-brand-deep">10 ans sur les façades du Périgord.</h2>
            <div className="mt-8 space-y-6 text-[16px] text-ink-secondary leading-relaxed">
              <p>
                Brandon a commencé sur les chantiers il y a dix ans. Ravalement, nettoyage, peinture : il a appris le métier sur le terrain, façade après façade.
              </p>
              <p>
                Aujourd'hui, HRC Rénovation intervient dans toute la Dordogne et les départements voisins. La méthode n'a pas changé : on vient chez vous, on regarde le support, on vous dit ce qui est nécessaire — et ce qui ne l'est pas.
              </p>
              <p>
                Pas de devis gonflé, pas de travaux inutiles. Un chiffrage clair sous 48h, des dates tenues, un chantier propre. C'est comme ça qu'on travaille, et c'est pour ça que nos clients nous recommandent à leurs voisins.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-surface-muted">
        <div className="max-w-site mx-auto px-5 lg:px-6">
          <RevealStagger className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Clock, title: "10 ans d'expérience", text: "Des dizaines de façades ravalées, nettoyées et repeintes en Dordogne et alentours." },
              { icon: ShieldCheck, title: "Devis clair sous 48h", text: "Un chiffrage détaillé et gratuit, remis sous 48h après la visite. Vous décidez ensuite." },
              { icon: MapPin, title: "Ancré localement", text: `Siège à Coulounieix-Chamiers. SIREN ${SITE.siren}. Un artisan d'ici, joignable directement.` },
            ].map(({ icon: Icon, title, text }) => (
              <div key={title} className="bg-white rounded-2xl p-8 border border-line shadow-subtle">
                <div className="w-14 h-14 rounded-xl bg-brand-highlight flex items-center justify-center mb-6">
                  <Icon size={26} className="text-brand-royal" aria-hidden="true" />
                </div>
                <h3 className="font-heading font-semibold text-xl text-brand-deep">{title}</h3>
                <p className="mt-3 text-[15px] text-ink-secondary">{text}</p>
              </div>
            ))}
          </RevealStagger>
        </div>
      </section>

      <CtaFinal />
    </>
  );
}
